import { useId } from 'react'
import type { CSSProperties } from 'react'

type Trend = 'up' | 'down' | 'flat'

interface SparklineProps {
  /** Series values, oldest → newest. Needs ≥ 2 points to draw. */
  points: number[]
  width?: number
  height?: number
  /** Stroke color. Defaults to a trend-derived token (down=danger, up=accent). */
  color?: string
  /** Override the auto-derived trend used for the default color. */
  trend?: Trend
  /** Fill the area under the line with a fading gradient of the stroke color. */
  area?: boolean
  /** Optional horizontal reference line (e.g. "your price"), in series units. */
  baseline?: number
  /** Draw a dot on the latest point. */
  endDot?: boolean
  strokeWidth?: number
  className?: string
  style?: CSSProperties
}

function deriveTrend(points: number[]): Trend {
  if (points.length < 2) return 'flat'
  const delta = points[points.length - 1] - points[0]
  const span = Math.max(...points) - Math.min(...points) || 1
  if (delta / span > 0.02) return 'up'
  if (delta / span < -0.02) return 'down'
  return 'flat'
}

const TREND_COLOR: Record<Trend, string> = {
  up: 'var(--accent)',
  down: 'var(--danger)',
  flat: 'var(--text-2)',
}

/**
 * Sparkline — the 30-day mini price chart used in price rows and rival columns.
 * The one design-system primitive that lives only in markup (not globals.css),
 * so this is the canonical implementation. Aurora+ source: s07 Price Intelligence.
 *
 * Pure SVG, no deps, theme-aware via CSS-variable colors. preserveAspectRatio
 * "none" lets the caller size it freely with width/height or CSS.
 */
export function Sparkline({
  points,
  width = 120,
  height = 32,
  color,
  trend,
  area = true,
  baseline,
  endDot = true,
  strokeWidth = 1.8,
  className,
  style,
}: SparklineProps) {
  const gradId = useId()

  if (!points || points.length < 2) {
    return <svg width={width} height={height} className={className} style={style} aria-hidden />
  }

  const pad = 4
  const minY = Math.min(...points, ...(baseline != null ? [baseline] : []))
  const maxY = Math.max(...points, ...(baseline != null ? [baseline] : []))
  const range = maxY - minY || 1

  const x = (i: number) => pad + (i / (points.length - 1)) * (width - pad * 2)
  const y = (v: number) => pad + (1 - (v - minY) / range) * (height - pad * 2)

  const line = points.map((p, i) => `${x(i)},${y(p)}`).join(' ')
  const areaPath = `M ${pad},${height - pad} L ${points
    .map((p, i) => `${x(i)},${y(p)}`)
    .join(' L ')} L ${width - pad},${height - pad} Z`

  const stroke = color ?? TREND_COLOR[trend ?? deriveTrend(points)]
  const lastX = x(points.length - 1)
  const lastY = y(points[points.length - 1])

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      preserveAspectRatio="none"
      className={className}
      style={style}
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity={0.28} />
          <stop offset="100%" stopColor={stroke} stopOpacity={0} />
        </linearGradient>
      </defs>
      {baseline != null && (
        <line
          x1={pad}
          y1={y(baseline)}
          x2={width - pad}
          y2={y(baseline)}
          stroke="var(--accent)"
          strokeWidth={1}
          strokeDasharray="3,3"
          opacity={0.6}
        />
      )}
      {area && <path d={areaPath} fill={`url(#${gradId})`} />}
      <polyline
        points={line}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {endDot && (
        <circle cx={lastX} cy={lastY} r={3} fill={stroke} stroke="var(--surface-solid)" strokeWidth={2} />
      )}
    </svg>
  )
}
